import type { ContextSource, InterviewConfig, Message, ProjectDetail, ProjectShare, ProjectSummary, SessionLog, SessionSummary, VocabularyReportCard, VoiceCast } from '../types';

const baseUrl = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

async function request<T>(path: string, token: string | undefined, init?: RequestInit): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${response.status})`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export function completeSession(token: string | undefined, sessionId: string, config: InterviewConfig, messages: Message[], durationSeconds: number) {
  return request<{ reportCard: VocabularyReportCard }>('/api/sessions/complete', token, {
    method: 'POST',
    body: JSON.stringify({ sessionId, config, messages, durationSeconds }),
  });
}

export function abandonSession(token: string | undefined, sessionId: string) {
  return request<void>(`/api/sessions/${encodeURIComponent(sessionId)}`, token, { method: 'DELETE' });
}

export function fetchSessions(token: string | undefined) {
  return request<{ sessions: SessionSummary[] }>('/api/sessions', token);
}

export function fetchSession(token: string | undefined, id: string) {
  return request<SessionLog>(`/api/sessions/${encodeURIComponent(id)}`, token);
}

export async function fetchReplayAudio(token: string | undefined, replayUrl: string) {
  if (!replayUrl.startsWith('/api/sessions/')) throw new Error('Invalid replay URL');
  const response = await fetch(`${baseUrl}${replayUrl}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Replay request failed (${response.status})`);
  }
  return response.blob();
}

export function saveSessionConfig(token: string | undefined, id: string, config: InterviewConfig, start = false, projectId?: string) {
  return request<{ session: SessionSummary }>(`/api/sessions/${encodeURIComponent(id)}`, token, {
    method: 'PUT',
    body: JSON.stringify({ config, start, projectId }),
  });
}

export function fetchProjects(token: string | undefined) {
  return request<{ projects: ProjectSummary[] }>('/api/projects', token);
}

export function createProject(token: string | undefined, options: { id?: string; initialPrompt?: string; fromSessionId?: string } = {}) {
  return request<ProjectDetail>('/api/projects', token, { method: 'POST', body: JSON.stringify(options) });
}

export function fetchProject(token: string | undefined, id: string) {
  return request<ProjectDetail>(`/api/projects/${encodeURIComponent(id)}`, token);
}

export function deleteProject(token: string | undefined, id: string) {
  return request<{ status: string }>(`/api/projects/${encodeURIComponent(id)}`, token, { method: 'DELETE' });
}

export function updateProjectContext(token: string | undefined, id: string, contextIds: string[]) {
  return request<ProjectDetail>(`/api/projects/${encodeURIComponent(id)}/context`, token, {
    method: 'PUT',
    body: JSON.stringify({ contextIds }),
  });
}

export function updateProjectTitle(token: string | undefined, id: string, title: string) {
  return request<ProjectDetail>(`/api/projects/${encodeURIComponent(id)}/title`, token, {
    method: 'PUT',
    body: JSON.stringify({ title }),
  });
}

export function updateProjectVoiceCast(token: string | undefined, id: string, voiceCast: VoiceCast) {
  return request<ProjectDetail>(`/api/projects/${encodeURIComponent(id)}/voice-cast`, token, {
    method: 'PUT',
    body: JSON.stringify({ voiceCast }),
  });
}

export function sendSetupMessage(token: string | undefined, id: string, text: string) {
  return request<ProjectDetail>(`/api/projects/${encodeURIComponent(id)}/messages`, token, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

export function fetchProjectShares(token: string | undefined, id: string) {
  return request<{ shares: ProjectShare[] }>(`/api/projects/${encodeURIComponent(id)}/shares`, token);
}

export function shareProject(token: string | undefined, id: string, email: string, accessRole: 'viewer' | 'editor') {
  return request<{ share: ProjectShare; invitePath: string }>(`/api/projects/${encodeURIComponent(id)}/shares`, token, {
    method: 'POST',
    body: JSON.stringify({ email, accessRole }),
  });
}

export function duplicateProject(token: string | undefined, id: string) {
  return request<ProjectDetail>(`/api/projects/${encodeURIComponent(id)}/duplicate`, token, { method: 'POST' });
}

export type AdminOverview = {
  range: { start: string; end: string };
  totals: { users: number; sessions: number; completed: number; minutes: number };
  modes: Array<{ mode: string; sessions: number }>;
  roles: Array<{ role: string; sessions: number }>;
  recent: SessionSummary[];
  journeys: Array<{
    userId: string;
    email: string | null;
    firstSeenAt: string;
    lastSeenAt: string;
    events: JourneyEvent[];
  }>;
};

export type JourneyEventName = 'signup_completed' | 'signin_completed' | 'signout_completed' | 'app_opened' | 'practice_setup_viewed' | 'practice_launched' | 'practice_started'
  | 'context_created' | 'context_selected' | 'voice_connected' | 'first_answer_submitted'
  | 'session_completed' | 'session_log_viewed';

export type JourneyEvent = {
  id: string;
  eventName: JourneyEventName;
  sessionId: string | null;
  properties: Record<string, string | number | boolean>;
  occurredAt: string;
};

export function fetchAdminOverview(token: string | undefined, start: string, end: string) {
  const query = new URLSearchParams({ start, end });
  return request<AdminOverview>(`/api/admin/overview?${query}`, token);
}

export type AdminUser = {
  userId: string;
  email: string | null;
  joinedAt: string;
  lastActiveAt: string;
  sessionCount: number;
  completedCount: number;
  totalMinutes: number;
  journeyEventCount: number;
};

export type AdminUserDetails = {
  user: Pick<AdminUser, 'userId' | 'email' | 'joinedAt'>;
  events: JourneyEvent[];
  sessions: Array<Pick<SessionLog, 'session' | 'transcript'>>;
};

export function fetchAdminUsers(token: string | undefined) {
  return request<{ users: AdminUser[] }>('/api/admin/users', token);
}

export function fetchAdminUser(token: string | undefined, userId: string) {
  return request<AdminUserDetails>(`/api/admin/users/${encodeURIComponent(userId)}`, token);
}

export function recordJourneyEvent(
  token: string | undefined,
  eventName: JourneyEventName,
  options: { sessionId?: string; properties?: Record<string, string | number | boolean>; clientEventId?: string } = {},
) {
  return request<{ event: JourneyEvent; created: boolean }>('/api/journey-events', token, {
    method: 'POST',
    body: JSON.stringify({
      eventName,
      clientEventId: options.clientEventId || crypto.randomUUID(),
      sessionId: options.sessionId,
      properties: options.properties,
    }),
  });
}

export function deleteAccount(token: string | undefined) {
  return request<{ status: string }>('/api/account', token, { method: 'DELETE' });
}

export function fetchContextSources(token: string | undefined) {
  return request<{ contextSources: ContextSource[] }>('/api/context', token);
}

export function addTextContext(token: string | undefined, text: string) {
  return request<{ contextSource: ContextSource }>('/api/context/text', token, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

export function addDriveFolderContext(token: string | undefined, folderName: string, contextIds: string[]) {
  return request<{ contextSource: ContextSource }>('/api/context/drive-folders', token, {
    method: 'POST',
    body: JSON.stringify({ folderName, contextIds }),
  });
}

export function startDriveFolderContext(token: string | undefined, projectId: string, folder: { id: string; name: string; resourceKey?: string }) {
  return request<{ contextSource: ContextSource }>('/api/context/drive-folders/start', token, {
    method: 'POST',
    body: JSON.stringify({ projectId, folderId: folder.id, folderName: folder.name, resourceKey: folder.resourceKey }),
  });
}

export function updateDriveFolderProgress(token: string | undefined, id: string, progress: { folderName: string; contextIds: string[]; fileCount: number; downloadedBytes: number; skippedCount: number; scanTruncated: boolean }) {
  return request<{ contextSource: ContextSource }>(`/api/context/drive-folders/${encodeURIComponent(id)}/progress`, token, {
    method: 'PUT', body: JSON.stringify(progress),
  });
}

export function completeDriveFolderContext(token: string | undefined, id: string, folderName: string, contextIds: string[]) {
  return request<{ contextSource: ContextSource }>(`/api/context/drive-folders/${encodeURIComponent(id)}/complete`, token, {
    method: 'POST', body: JSON.stringify({ folderName, contextIds }),
  });
}

export function failDriveFolderContext(token: string | undefined, id: string, error: string) {
  return request<{ contextSource: ContextSource }>(`/api/context/drive-folders/${encodeURIComponent(id)}/fail`, token, {
    method: 'POST', body: JSON.stringify({ error }),
  });
}

export function addRepositoryContext(token: string | undefined, url: string) {
  return request<{ contextSource: ContextSource }>('/api/context/repositories', token, {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
}

export function createContextUpload(token: string | undefined, file: File, projectId?: string) {
  return request<{
    contextSource: ContextSource;
    upload: { url: string; method: string; headers: Record<string, string>; expiresAt: number };
  }>('/api/context/uploads', token, {
    method: 'POST',
    body: JSON.stringify({ fileName: file.name, contentType: file.type || undefined, sizeBytes: file.size, projectId }),
  });
}

export function completeContextUpload(token: string | undefined, id: string) {
  return request<{ contextSource: ContextSource }>(`/api/context/uploads/${id}/complete`, token, { method: 'POST' });
}
